import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.jsx'

const DEFAULT_SENTRY_DSN =
  'https://7ef0166df0701b20485b76c2e27b6fa1@o4511216888512512.ingest.de.sentry.io/4511228364652624'
const DEFAULT_REPLAYS_SESSION_SAMPLE_RATE = 1.0
const DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE = 1.0

const sentryDsn = import.meta.env.VITE_SENTRY_DSN || DEFAULT_SENTRY_DSN
const sentryEnvironment = import.meta.env.VITE_ENV || import.meta.env.MODE
const sentryTraceSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0)
const replaysSessionSampleRate = Number.isFinite(
  Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
)
  ? Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE)
  : DEFAULT_REPLAYS_SESSION_SAMPLE_RATE
const replaysOnErrorSampleRate = Number.isFinite(
  Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE),
)
  ? Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE)
  : DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE

console.log('[Sentry Setup][Runtime][1] Starting runtime setup')
console.log('[Sentry Setup][Runtime][2] VITE_SENTRY_DSN:', sentryDsn)
console.log('[Sentry Setup][Runtime][3] VITE_ENV:', import.meta.env.VITE_ENV)
console.log('[Sentry Setup][Runtime][4] MODE:', import.meta.env.MODE)
console.log('[Sentry Setup][Runtime][5] VITE_SENTRY_TRACES_SAMPLE_RATE:', import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE)
console.log('[Sentry Setup][Runtime][6] Resolved environment:', sentryEnvironment)
console.log('[Sentry Setup][Runtime][7] Resolved tracesSampleRate:', sentryTraceSampleRate)
console.log('[Sentry Setup][Runtime][8] Resolved replaysSessionSampleRate:', replaysSessionSampleRate)
console.log('[Sentry Setup][Runtime][9] Resolved replaysOnErrorSampleRate:', replaysOnErrorSampleRate)
console.log('[Sentry Setup][Runtime][10] Sentry enabled:', Boolean(sentryDsn))

Sentry.init({
  dsn: sentryDsn,
  enabled: Boolean(sentryDsn),
  sendDefaultPii: true,
  integrations: (defaultIntegrations) => {
    const withoutDedupe = defaultIntegrations.filter(
      (integration) => integration.name !== 'Dedupe',
    )
    return [
      ...withoutDedupe,
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ]
  },
  tracesSampleRate: sentryTraceSampleRate,
  replaysSessionSampleRate,
  replaysOnErrorSampleRate,
  environment: sentryEnvironment,
  // debug: import.meta.env.DEV, // re-enable if Sentry logs are needed again
})

console.log('[Sentry Setup][Runtime][11] Sentry.init completed')

// Capture fetch failures (network errors) and 5xx responses globally.
if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const originalFetch = window.fetch.bind(window)
  window.fetch = async (...args) => {
    let url = 'unknown'
    let method = 'GET'
    try {
      const [input, init] = args
      if (typeof input === 'string') url = input
      else if (input && typeof input === 'object' && 'url' in input) url = input.url
      if (init?.method) method = String(init.method).toUpperCase()

      const res = await originalFetch(...args)

      if (sentryDsn && res && !res.ok && typeof res.status === 'number' && res.status >= 500) {
        Sentry.withScope((scope) => {
          scope.setTag('api_transport', 'fetch')
          scope.setTag('http_status', String(res.status))
          scope.setContext('request', { url, method })
          Sentry.captureException(new Error(`Fetch ${res.status} ${method} ${url}`))
        })
      }

      return res
    } catch (error) {
      if (sentryDsn) {
        Sentry.withScope((scope) => {
          scope.setTag('api_transport', 'fetch')
          scope.setTag('http_status', 'network_error')
          scope.setContext('request', { url, method })
          Sentry.captureException(error)
        })
      }
      throw error
    }
  }
}

window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event?.reason || new Error('Unhandled promise rejection'))
})

window.addEventListener('error', (event) => {
  if (event?.error) {
    Sentry.captureException(event.error)
  }
})

function SentryTestButton() {
  if (!import.meta.env.DEV) return null
  if (!sentryDsn) return null

  const sendTestError = async () => {
    console.log('[Sentry Setup][Runtime][12] Sending manual test exception')
    const eventId = Sentry.captureException(new Error('Manual Sentry test error from Break App button'))
    console.log('[Sentry Setup][Runtime][13] Test exception eventId:', eventId)
    await Sentry.flush(3000)
    console.log('[Sentry Setup][Runtime][14] Flush finished for test exception')
  }

  return (
    <button
      type="button"
      onClick={async () => {
        await sendTestError()
        throw new Error('Test Sentry Error, is it working???:{{')
      }}
      style={{
        position: 'fixed',
        right: '16px',
        bottom: '16px',
        zIndex: 9999,
        padding: '8px 12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        background: '#ef4444',
        color: '#fff',
        cursor: 'pointer',
      }}
    >
      Break App
    </button>
  )
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>}>
      <App />
      <SentryTestButton />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
